// realtime.service.ts
import { Injectable, OnDestroy, NgZone } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { BehaviorSubject, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { GlobalService } from './global.service';

@Injectable({ providedIn: 'root' })
export class RealtimeService implements OnDestroy {
  private socket?: Socket;
  private connected = false;
  private activeCustomerRooms = new Set<number>();
  private inFeedbackList = false;

  // Estado da ligação Socket.IO
  isConnected$ = new BehaviorSubject<boolean>(false);

  // streams...
  ticketCreated$ = new BehaviorSubject<any | null>(null);
  ticketPatched$ = new BehaviorSubject<{ id: number; patch: any } | null>(null);
  detailAdded$ = new BehaviorSubject<any | null>(null);
  typing$ = new BehaviorSubject<any | null>(null);
  ticketFilesChanged$ = new BehaviorSubject<{
    idMessageCenterTransaction: number;
    idMessageCenterTransactionDetail: number;
    idMessageCenterTransactionFiles: number;
    name: string;
    size: any;
    type: string;
    op: string;
  } | null>(null);

  customerCreated$ = new BehaviorSubject<any | null>(null);
  customerPatched$ = new BehaviorSubject<{ id: number; patch: any } | null>(null);
  customerSectionChanged$ = new BehaviorSubject<any | null>(null);
  customerFilesChanged$ = new BehaviorSubject<any | null>(null);

  // Calls streams
  incomingCall$ = new BehaviorSubject<{ number: string } | null>(null);

  // Insurance Policy streams
  policySaved$ = new BehaviorSubject<any | null>(null);
  policyFilesChanged$ = new Subject<any>();

  // Insurance Claims streams
  claimSaved$ = new Subject<any>();
  claimFilesChanged$ = new Subject<any>();

  // SafeSimulations / Quotes streams
  safeSimulationSaved$ = new Subject<any>();

  // Global / Client Area Catalog, Companies, Hub & Referrals streams
  companyUpdated$ = new Subject<any>();
  catalogUpdated$ = new Subject<any>();
  hubUpdated$ = new Subject<any>();
  referralUpdated$ = new Subject<any>();

  // Unified trigger for Client Area alerts & badges
  clientAreaAlertsRefresh$ = new Subject<any>();

  // Email Template streams
  templateSaved$ = new BehaviorSubject<any | null>(null);
  templateDeleted$ = new BehaviorSubject<any | null>(null);

  // Admin Feedback streams
  feedbackCreated$ = new BehaviorSubject<any | null>(null);
  feedbackUpdated$ = new BehaviorSubject<{ id: number; patch: any } | null>(null);
  feedbackDeleted$ = new BehaviorSubject<{ id: number } | null>(null);

  // Claim Mobile Signature streams (Feedback #449)
  claimSignatureReceived$ = new BehaviorSubject<any | null>(null);

  constructor(
    private zone: NgZone,
    private globalService: GlobalService,
  ) {}

  connect(): void {
    if (this.connected) return;

    // 🔒 Security Guard: Only allow socket connection if user is authenticated
    const token = this.globalService.loggedUserClass?.Token;
    if (!token) {
      // console.debug('[Realtime] Conexão abortada: Utilizador não autenticado.');
      return;
    }

    let urlConfig = environment.URL;
    let base: string;

    // Se a URL do ambiente começar com http (produção), usa essa origem
    if (urlConfig && urlConfig.startsWith('http')) {
      try {
        base = new URL(urlConfig).origin;
      } catch {
        base = window.location.origin;
      }
    } else {
      // Em desenvolvimento, apontar diretamente para a API (8443)
      // para evitar problemas com proxy de WebSockets do Angular CLI
      base = 'http://localhost:8443';
    }

    this.socket = io(base, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      withCredentials: true,
      auth: {
        token: token,
        appVersion: environment.appVersion,
      },
    });

    this.socket.on('connect', () => {
      this.zone.run(() => {
        this.connected = true;
        this.isConnected$.next(true);
        this.socket!.emit('list:join');
        if (this.inFeedbackList) {
          this.socket!.emit('feedback:join');
        }
        this.activeCustomerRooms.forEach(idCustomer => {
          if (idCustomer > 0) {
            this.socket!.emit('customer:join', idCustomer);
          }
        });
      });
    });

    this.socket.on('disconnect', _reason => {
      this.zone.run(() => {
        this.connected = false;
        this.isConnected$.next(false);
      });
    });

    // ---- Tickets ----
    this.socket.on('ticket:created', t =>
      this.zone.run(() => {
        this.ticketCreated$.next(t);
      }),
    );
    this.socket.on('ticket:patched', p =>
      this.zone.run(() => {
        this.ticketPatched$.next(p);
      }),
    );
    this.socket.on('detail:added', d =>
      this.zone.run(() => {
        this.detailAdded$.next(d);
      }),
    );
    this.socket.on('presence:typing', x =>
      this.zone.run(() => {
        this.typing$.next(x);
      }),
    );
    this.socket.on('files:changed', payload =>
      this.zone.run(() => {
        this.ticketFilesChanged$.next(payload);
      }),
    );

    // ---- Customers ----
    this.socket.on('customer:created', row =>
      this.zone.run(() => {
        this.customerCreated$.next(row);
      }),
    );
    this.socket.on('customer:patched', p =>
      this.zone.run(() => {
        this.customerPatched$.next(p);
        this.clientAreaAlertsRefresh$.next({ source: 'customer:patched', payload: p });
      }),
    );
    this.socket.on('customer:sectionChanged', e =>
      this.zone.run(() => {
        this.customerSectionChanged$.next(e);
        this.clientAreaAlertsRefresh$.next({ source: 'customer:sectionChanged', payload: e });
      }),
    );
    this.socket.on('customer:filesChanged', payload =>
      this.zone.run(() => {
        this.customerFilesChanged$.next(payload);
        this.clientAreaAlertsRefresh$.next({ source: 'customer:filesChanged', payload });
      }),
    );

    // ---- Insurance Policies ----
    this.socket.on('policy:saved', payload =>
      this.zone.run(() => {
        this.policySaved$.next(payload);
        this.clientAreaAlertsRefresh$.next({ source: 'policy:saved', payload });
      }),
    );
    this.socket.on('policy:filesChanged', payload =>
      this.zone.run(() => {
        this.policyFilesChanged$.next(payload);
        this.clientAreaAlertsRefresh$.next({ source: 'policy:filesChanged', payload });
      }),
    );

    // ---- Insurance Claims ----
    this.socket.on('claim:saved', payload =>
      this.zone.run(() => {
        this.claimSaved$.next(payload);
        this.clientAreaAlertsRefresh$.next({ source: 'claim:saved', payload });
      }),
    );
    this.socket.on('claim:filesChanged', payload =>
      this.zone.run(() => {
        this.claimFilesChanged$.next(payload);
        this.clientAreaAlertsRefresh$.next({ source: 'claim:filesChanged', payload });
      }),
    );

    // ---- SafeSimulations ----
    this.socket.on('safesimulation:saved', payload =>
      this.zone.run(() => {
        this.safeSimulationSaved$.next(payload);
      }),
    );

    // ---- Companies / Catalog / Hub / Referrals ----
    this.socket.on('company:updated', payload =>
      this.zone.run(() => {
        this.companyUpdated$.next(payload);
      }),
    );
    this.socket.on('catalog:updated', payload =>
      this.zone.run(() => {
        this.catalogUpdated$.next(payload);
      }),
    );
    this.socket.on('hub:updated', payload =>
      this.zone.run(() => {
        this.hubUpdated$.next(payload);
      }),
    );
    this.socket.on('referral:updated', payload =>
      this.zone.run(() => {
        this.referralUpdated$.next(payload);
      }),
    );

    // ---- Calls ----
    this.socket.on('incomingCall', payload =>
      this.zone.run(() => {
        this.incomingCall$.next(payload);
      }),
    );

    // ---- Email Templates ----
    this.socket.on('template:saved', payload =>
      this.zone.run(() => {
        this.templateSaved$.next(payload);
      }),
    );
    this.socket.on('template:deleted', payload =>
      this.zone.run(() => {
        this.templateDeleted$.next(payload);
      }),
    );

    // ---- Admin Feedback ----
    this.socket.on('feedback:created', payload =>
      this.zone.run(() => {
        this.feedbackCreated$.next(payload);
      }),
    );
    this.socket.on('feedback:updated', payload =>
      this.zone.run(() => {
        this.feedbackUpdated$.next(payload);
      }),
    );
    this.socket.on('feedback:deleted', payload =>
      this.zone.run(() => {
        this.feedbackDeleted$.next(payload);
      }),
    );

    // ---- Claim Mobile Signature (Feedback #449) ----
    this.socket.on('claim:signature_received', payload =>
      this.zone.run(() => {
        this.claimSignatureReceived$.next(payload);
      }),
    );
  }

  // helpers
  joinCustomersList() {
    this.socket?.emit('customers:join');
  }
  joinCustomer(idCustomer: number) {
    const id = Number(idCustomer);
    if (!id || id <= 0) return;
    this.activeCustomerRooms.add(id);
    this.socket?.emit('customer:join', id);
  }
  leaveCustomer(idCustomer: number) {
    const id = Number(idCustomer);
    if (!id || id <= 0) return;
    this.activeCustomerRooms.delete(id);
    this.socket?.emit('customer:leave', id);
  }
  joinTicket(id: number) {
    this.socket?.emit('ticket:join', id);
  }

  leaveTicket(id: number) {
    this.socket?.emit('ticket:leave', id);
  }
  joinRoom(room: string) {
    this.socket?.emit('join', { room });
  }
  leaveRoom(room: string) {
    this.socket?.emit('leave', { room });
  }
  joinEmailTemplates() {
    this.socket?.emit('emailtemplates:join');
  }
  leaveEmailTemplates() {
    this.socket?.emit('emailtemplates:leave');
  }
  joinFeedbackList() {
    this.inFeedbackList = true;
    if (!this.connected) {
      this.connect();
    }
    if (this.connected && this.socket) {
      this.socket.emit('feedback:join');
    }
  }
  leaveFeedbackList() {
    this.inFeedbackList = false;
    this.socket?.emit('feedback:leave');
  }
  isRealtimeConnected(): boolean {
    return this.connected;
  }
  joinClaimSignature(sessionId: string) {
    if (sessionId) {
      this.socket?.emit('claim-signature:join', sessionId);
    }
  }
  leaveClaimSignature(sessionId: string) {
    if (sessionId) {
      this.socket?.emit('claim-signature:leave', sessionId);
    }
  }

  /**
   * Atualiza o token do Socket e reconecta se necessário
   * @param token Novo token JWT
   */
  updateToken(token: string): void {
    if (this.socket) {
      this.socket.auth = { token: token };
      if (this.connected) {
        // Desconectar e reconectar para aplicar o novo token no handshake
        this.socket.disconnect().connect();
      }
    }
  }
  ngOnDestroy() {
    this.socket?.close();
    // NÃO completar subjects - serviço singleton pode ser reutilizado
    // Apenas fechar o socket é suficiente
  }
}
